/**
 * ContextMenu: 참여자 타일 우클릭 메뉴.
 *
 * v1 원본: RenewalComponents/SVContent/Common/ContextMenu/ + useScreenPinManager
 * v2 변환: 단일 함수형 컴포넌트
 *
 * 메뉴 항목 (기획서 기준):
 * - 본인 화면 우클릭:                    화면고정
 * - 비마스터 → 타인 우클릭:              화면고정, 음소거(행위자한테만)
 * - 마스터 → 타인 우클릭:               화면고정, 음소거, 내보내기
 *
 * 화면 고정 정책 (v1 useScreenPinManager 기반):
 * - 마스터 핀 + 개인 핀 합산 최대 2개
 * - 마스터가 핀 클릭 시 → LUXDialog "모두에게 화면 고정" vs "나에게만 화면 고정"
 * - 마스터 고정 → UPDATE_MASTER_PIN_FIXED 브로드캐스트 → 모두 PRESENTER 뷰 전환
 * - 사용자 고정 → 로컬만 PRESENTER 뷰 전환
 * - 마스터가 고정한 화면은 일반 사용자가 해제 불가
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { mainUserActions } from '@/store/slices/mainUserSlice';
import { participantsActions } from '@/store/slices/participantsSlice';
import { uiActions } from '@/store/slices/uiSlice';
import { isMasterUser } from '@/store/slices/masterSlice';
import { toastActions } from '@/store/slices/toastSlice';
import LUXDialog from '@/components/Dialog/LUXDialog';
import { getConferenceManager } from '@/conference/conferenceManagerRef';
import { CONFERENCE_COMMANDS } from '@/conference/commands';
import { getSyncedNow } from '@/utils/timeOffset';

interface ContextMenuProps {
  userId: string;
  /** 참여자 타일의 container id (contextmenu 이벤트 바인딩 대상) */
  containerId: string;
  onKickParticipant?: (userId: string) => void;
  /** true: 공유 화면 타일 (화면 공유 트랙 표시) */
  isScreenShare?: boolean;
}

const MAX_PIN_COUNT = 2;

/** 핀 복합 키: 화면 공유는 'userId:screen', 카메라는 'userId' */
const toPinKey = (userId: string, isScreenShare?: boolean) =>
  isScreenShare ? `${userId}:screen` : userId;

const parsePinKey = (pinKey: string): { userId: string; isScreenShare: boolean } => {
  if (pinKey.endsWith(':screen')) {
    return { userId: pinKey.slice(0, -7), isScreenShare: true };
  }
  return { userId: pinKey, isScreenShare: false };
};

const ContextMenu: React.FC<ContextMenuProps> = ({ userId, containerId, onKickParticipant, isScreenShare = false }) => {
  const dispatch = useAppDispatch();
  const local = useAppSelector((s) => s.local);
  const masterUsers = useAppSelector((s) => s.master.masterUsers);
  const mainUser = useAppSelector((s) => s.mainUser);
  const participant = useAppSelector((s) => s.participants.participants[userId]);
  const localMutedIds = useAppSelector((s) => s.participants.localMutedIds);

  const isLocalMaster = isMasterUser(masterUsers, local.wehagoId);
  // userId가 local.jitsiId, local.userId, 또는 'local' 중 하나면 로컬 사용자
  const isLocalUser = userId === local.jitsiId || userId === local.userId || userId === 'local';

  const pinKey = toPinKey(userId, isScreenShare);
  const isMasterPinned = mainUser.masterPinnedUserIds.includes(pinKey);
  const isUserPinned = mainUser.pinnedUserIds.includes(pinKey);
  const isPinned = isMasterPinned || isUserPinned;

  const isAudioMuted = participant?.tracks?.audioMuted ?? false;
  const isLocallyMuted = localMutedIds.includes(userId);

  // 참여자 기본 이름
  const baseParticipantName = isLocalUser
    ? (local.userName || local.nickname || '')
    : (participant?.displayName || participant?.nickname || '');

  // 공유 화면/일반 화면에 따른 표시 이름 결정
  // 본인의 공유 화면: "나의 공유 화면"
  // 타인의 공유 화면: "OOO님의 공유 화면"
  // 일반 화면: "OOO"
  const participantName = isScreenShare
    ? (isLocalUser ? '나의 공유 화면' : `${baseParticipantName}님의 공유 화면`)
    : baseParticipantName;

  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const menuRef = useRef<HTMLDivElement>(null);

  const showSnackbar = useCallback((type: 'success' | 'error' | 'info', message: string) => {
    dispatch(toastActions.showSnackbar({ type, message }));
  }, [dispatch]);

  // ─── 우클릭 이벤트 ─────────────────────────────────────────────────────
  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container) return;

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      const rect = container.getBoundingClientRect();
      setPosition({
        x: Math.min(e.clientX - rect.left, rect.width - 160),
        y: Math.min(e.clientY - rect.top, rect.height - 120),
      });
      setIsOpen(true);
    };

    container.addEventListener('contextmenu', handleContextMenu);
    return () => container.removeEventListener('contextmenu', handleContextMenu);
  }, [containerId]);

  // ─── 외부 클릭 닫기 ───────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // ─── 핀 헬퍼 ─────────────────────────────────────────────────────────

  /** 마스터 핀 고정/해제 (브로드캐스트) — v1 handleMasterPin */
  const doMasterPin = useCallback((type: 'pin' | 'unpin') => {
    const currentMasterPinned = mainUser.masterPinnedUserIds;
    let nextPinned: string[];

    if (type === 'pin') {
      if (currentMasterPinned.includes(pinKey)) return;
      nextPinned = [...currentMasterPinned, pinKey].slice(-MAX_PIN_COUNT);
    } else {
      nextPinned = currentMasterPinned.filter((id) => id !== pinKey);
    }

    if (nextPinned.length > MAX_PIN_COUNT) {
      showSnackbar('error', `화면은 최대 ${MAX_PIN_COUNT}개까지 고정가능합니다.`);
      return;
    }

    dispatch(mainUserActions.setMasterPinnedUsers(nextPinned));

    // 브로드캐스트
    const room = getConferenceManager()?.getRoom?.();
    if (room) {
      // 브로드캐스트용: 로컬 사용자의 portal ID → Jitsi ID 변환 (수신측 매칭용)
      const jitsiId = room.myUserId();
      const broadcastPinned = nextPinned.map(key => {
        const { userId: uid, isScreenShare: ss } = parsePinKey(key);
        return toPinKey(uid === local.userId ? jitsiId : uid, ss);
      });
      room.sendCommand(CONFERENCE_COMMANDS.UPDATE_MASTER_PIN_FIXED, {
        value: jitsiId,
        attributes: {
          masterPinnedUsers: JSON.stringify(broadcastPinned),
          userName: baseParticipantName,
          type,
          isScreenShare: isScreenShare ? 'true' : 'false',
          actionTimestamp: getSyncedNow().toString(),
        },
      });
    }

    if (type === 'pin') {
      dispatch(uiActions.setScreenMode('PRESENTER'));
      showSnackbar('info', `화면이 고정되었습니다. ${nextPinned.length}/${MAX_PIN_COUNT}`);
    } else {
      showSnackbar('info', `화면 고정이 해제되었습니다. ${nextPinned.length}/${MAX_PIN_COUNT}`);
    }
  }, [dispatch, pinKey, baseParticipantName, mainUser.masterPinnedUserIds, showSnackbar, isScreenShare]);

  /** 개인 핀 고정/해제 (로컬만) — v1 handleUserPin */
  const doUserPin = useCallback((type: 'pin' | 'unpin') => {
    const currentPinned = mainUser.pinnedUserIds;
    const masterCount = mainUser.masterPinnedUserIds.length;

    if (type === 'unpin') {
      if (isMasterPinned) {
        showSnackbar('error', '마스터가 고정한 화면입니다.');
        return;
      }
      const nextPinned = currentPinned.filter((id) => id !== pinKey);
      dispatch(mainUserActions.setPinnedUsers(nextPinned));
      const totalPinned = masterCount + nextPinned.length;
      showSnackbar('info', `화면 고정이 해제되었습니다. ${totalPinned}/${MAX_PIN_COUNT}`);
      return;
    }

    // pin
    if (currentPinned.includes(pinKey)) return;
    const nextPinned = [...currentPinned, pinKey].slice(-MAX_PIN_COUNT);
    const totalPinned = masterCount + nextPinned.length;

    if (totalPinned > MAX_PIN_COUNT) {
      showSnackbar('error', `화면은 최대 ${MAX_PIN_COUNT}개까지 고정가능합니다.`);
      return;
    }

    dispatch(mainUserActions.setPinnedUsers(nextPinned));
    dispatch(uiActions.setScreenMode('PRESENTER'));
    showSnackbar('info', `화면이 고정되었습니다. ${totalPinned}/${MAX_PIN_COUNT}`);
  }, [dispatch, pinKey, isMasterPinned, mainUser.pinnedUserIds, mainUser.masterPinnedUserIds, showSnackbar]);

  // ─── 핸들러 ───────────────────────────────────────────────────────────

  const handlePin = useCallback(() => {
    setIsOpen(false);
    dispatch(uiActions.setIsShareClick(false));

    const allPinned = new Set([
      ...mainUser.masterPinnedUserIds,
      ...mainUser.pinnedUserIds,
      ...(isPinned ? [] : [pinKey]),
    ]);

    // 해제가 아닌 고정인데 합산 2개 초과
    if (!isPinned && allPinned.size > MAX_PIN_COUNT) {
      showSnackbar('error', `화면은 최대 ${MAX_PIN_COUNT}개까지 고정가능합니다.`);
      return;
    }

    const type = isPinned ? 'unpin' : 'pin';

    if (isLocalMaster) {
      if (type === 'pin' && !isMasterPinned) {
        // 마스터가 새로 고정 → "모두에게 / 나에게만" 선택 팝업 (v1 toggleMasterPinPopup)
        // 공유 화면인 경우: "나의 공유 화면을" / 일반 화면인 경우: "OOO님을"
        const dialogMessage = isScreenShare
          ? `${participantName}을\n모두에게 화면 고정 하시겠습니까?`
          : `${participantName}님을\n모두에게 화면 고정 하시겠습니까?`;
        LUXDialog.confirm(dialogMessage, {
          type: 'info',
          title: '화면 고정',
          confirmButtonLabel: '모두에게 화면 고정',
          cancelButtonLabel: '나에게만 화면 고정',
          confirmCallback: () => doMasterPin('pin'),
          cancelCallback: () => doUserPin('pin'),
        });
      } else if (isMasterPinned) {
        // 이미 마스터 핀된 항목 → 마스터 해제
        doMasterPin('unpin');
      } else {
        // 마스터지만 개인핀으로 되어있는 경우 → 개인 해제
        doUserPin(type);
      }
    } else {
      // 비마스터 → 개인 핀
      doUserPin(type);
    }
  }, [
    dispatch,
    userId,
    participantName,
    isLocalMaster,
    isPinned,
    isMasterPinned,
    mainUser.masterPinnedUserIds,
    mainUser.pinnedUserIds,
    doMasterPin,
    doUserPin,
    showSnackbar,
    isScreenShare,
  ]);

  const showMuted = isLocalMaster ? (isAudioMuted || isLocallyMuted) : isLocallyMuted;

  /** 마스터: 원격 음소거 / 비마스터: 행위자 로컬 음소거 */
  const handleMute = useCallback(() => {
    setIsOpen(false);
    if (isLocalMaster) {
      if (showMuted) {
        if (isAudioMuted) {
          getConferenceManager()?.unmuteRemoteAudio(userId);
        }
        if (isLocallyMuted) {
          dispatch(participantsActions.toggleLocalMuted(userId));
        }
      } else {
        LUXDialog.confirm(`${participantName}님을\n모두에게 음소거 처리 하시겠습니까?`, {
          type: 'question',
          title: '음소거 처리',
          confirmButtonLabel: '모두에게 음소거',
          cancelButtonLabel: '나에게만 음소거',
          confirmCallback: () => {
            getConferenceManager()?.muteRemoteAudio(userId);
          },
          cancelCallback: () => {
            dispatch(participantsActions.toggleLocalMuted(userId));
          },
        });
      }
    } else {
      dispatch(participantsActions.toggleLocalMuted(userId));
    }
  }, [dispatch, userId, isLocalMaster, showMuted, isAudioMuted, isLocallyMuted, participantName]);

  const handleKick = useCallback(() => {
    setIsOpen(false);
    LUXDialog.confirm(`${participantName}님을\n회의에서 내보내시겠습니까?`, {
      type: 'question',
      title: '참석자 내보내기',
      confirmButtonLabel: '내보내기',
      cancelButtonLabel: '취소',
      confirmCallback: () => {
        if (onKickParticipant) {
          onKickParticipant(userId);
        } else {
          getConferenceManager()?.kickParticipant(userId);
        }
        showSnackbar('success', `#${participantName}님을 내보냈습니다.`);
      },
    });
  }, [userId, participantName, onKickParticipant, showSnackbar]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="usermov_hoverLayer"
      style={{ top: position.y, left: position.x, zIndex: 1000 }}
    >
          {/* 화면 고정/해제 */}
          <button type="button" className="usermov_hoverLayer__btn" onClick={handlePin}>
            <span className={`usermov_hoverLayer__icon ${isPinned ? 'dispin' : 'pin'}`} />
            <span>{isPinned ? '화면 고정 해제' : '화면 고정'}</span>
          </button>

          {/* 음소거/해제 (타인만) */}
          {!isLocalUser && !(isLocalMaster && isAudioMuted && !isLocallyMuted) && (
            <button type="button" className="usermov_hoverLayer__btn" onClick={handleMute}>
              <span className={`usermov_hoverLayer__icon ${showMuted ? 'dismute' : 'mute'}`} />
              <span>{showMuted ? '음소거 해제' : '음소거'}</span>
            </button>
          )}

      {/* 내보내기 (마스터만, 자기 자신 제외) */}
      {isLocalMaster && !isLocalUser && (
        <button type="button" className="usermov_hoverLayer__btn" onClick={handleKick}>
          <span className="usermov_hoverLayer__icon kick" />
          <span>내보내기</span>
        </button>
      )}
    </div>
  );
};

export default ContextMenu;
